// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A table row component for displaying shipment information.
 * This module provides a row view that displays shipment details
 * including tracking information, locations, and timestamps.
 *
 * Key features:
 * - Responsive design with column visibility control
 * - Date formatting for shipment milestones
 * - Fallback displays for missing data
 * - Clickable row for future navigation
 */
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { Link } from '@/components/link';
import { LocationPairCell } from '@/components/location-pair-cell';
import { ShippingCarrierTrackingLabel } from '@/components/shipping-carrier-tracking-label';
import { StatusChip } from '@/components/status-chip';
import {
  InventoryOutlined,
  KeyboardTabOutlined,
  LocalShippingOutlined,
} from '@/constants/hardfin-standard-icons';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { Shipment, ShipmentItem } from '@/shipment/api/types';
import { MissingShipmentChip } from '@/shipment/components/missing-shipment-chip';
import { QuarantineShipmentChip } from '@/shipment/components/quarantine-shipment-chip';
import { ShipmentItemTypeIcon } from '@/shipment/components/shipment-item-type-icon';
import { ShipmentTimeInStatus } from '@/shipment/components/shipment-time-in-status';
import { ShipmentTotalValue } from '@/shipment/components/shipment-total-value';
import { SHIPMENT_STATUS_CONFIG } from '@/shipment/constants/shipment-status';
import {
  canCancel,
  canMarkDelivered,
  canMarkReceived,
  canMarkShipped,
} from '@/shipment/utilities/shipment-actions';
import { shipmentUnconfirmedTotal } from '@/shipment/utilities/shipment-unconfirmed-total';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { MouseEvent, useMemo, useState } from 'react';

/**
 * Renders quantity display based on shipment status and item data.
 * Shows expected quantities for pending/delivered shipments and received quantities with deltas for received shipments.
 */
function renderQuantityDisplay(
  item: ShipmentItem,
  receivedAt: DateTime | null
) {
  const isReceived = !!receivedAt;
  const plannedQty = item.quantity ?? 0;
  // Held units are excluded from the line's quantity, so add them back for the
  // total the shipment physically carried.
  const heldQty = item.quarantinedQuantity;
  const carriedQty = plannedQty + heldQty;

  if (!isReceived || item.receivedQuantity === null) {
    const plannedTooltip =
      heldQty > 0 ? (
        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={0.5}
          >
            <Typography variant="subtitle2">Accepted:</Typography>
            <Typography variant="body2">{formatNumber(plannedQty)}</Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={0.5}
          >
            <Typography variant="subtitle2">Quarantined:</Typography>
            <Typography variant="body2">{formatNumber(heldQty)}</Typography>
          </Stack>
        </Stack>
      ) : (
        'Planned quantity (not yet received)'
      );

    return (
      <Tooltip arrow title={plannedTooltip}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          &times; {formatNumber(carriedQty)}
        </Typography>
      </Tooltip>
    );
  }

  // Compared against what the shipment carried, held units included: the
  // pre-receipt branch shows that figure, so the delta has to use it too.
  const delta = item.receivedQuantity - carriedQty;
  const tooltipText = (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.5}
      >
        <Typography variant="subtitle2">Planned:</Typography>
        <Typography variant="body2">{formatNumber(carriedQty)}</Typography>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.5}
      >
        <Typography variant="subtitle2">Actual:</Typography>
        <Typography variant="body2">
          {formatNumber(item.receivedQuantity)}
        </Typography>
      </Stack>
      <Divider />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.5}
      >
        <Typography variant="subtitle2">Delta:</Typography>
        <Typography variant="body2">
          {delta > 0 ? '+' : ''}
          {delta}
        </Typography>
      </Stack>
      {heldQty > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={0.5}
        >
          <Typography variant="subtitle2">Quarantined:</Typography>
          <Typography variant="body2">{formatNumber(heldQty)}</Typography>
        </Stack>
      )}
    </Stack>
  );

  return (
    <ConditionalTooltip arrow tooltipText={tooltipText}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2">
          &times; {formatNumber(item.receivedQuantity)}
        </Typography>
        {delta !== 0 && (
          <Chip
            size="small"
            label={`${delta > 0 ? '+' : ''}${delta}`}
            color={delta > 0 ? 'success' : 'error'}
            sx={{ fontSize: '0.7rem' }}
          />
        )}
      </Stack>
    </ConditionalTooltip>
  );
}

function DateInfoRow({ label, date }: { label: string; date: string | null }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      width="100%"
      flexWrap="wrap"
      gap={0.5}
    >
      <Typography variant="subtitle2">{label}</Typography>
      {date ? (
        <Typography variant="body2">{date}</Typography>
      ) : (
        <Typography variant="body2" color="background.contrastText">
          Not set
        </Typography>
      )}
    </Stack>
  );
}

/**
 * Gets the most relevant date for a shipment based on its status
 * Inbound priority: Received > Delivered > Expected delivery (only if actually shipped) > Shipped (actual) > Expected ship (only if not actually shipped) > Expected delivery (if no expected ship and not shipped)
 * Outbound priority: Delivered > Expected delivery (only if actually shipped) > Shipped (actual) > Expected ship (only if not actually shipped) > Expected delivery (if no expected ship and not shipped)
 */
function getRelevantDate(shipment: Shipment, isOutbound: boolean) {
  // For inbound shipments, received date is the most relevant
  if (!isOutbound && shipment.receivedAt) {
    return {
      date: shipment.receivedAt.toLocaleString({ dateStyle: 'medium' }),
      label: null, // No label needed for received
    };
  }

  // For delivered shipments, show delivered date (applies to both inbound and outbound)
  if (shipment.deliveredAt) {
    return {
      date: shipment.deliveredAt.toLocaleString({ dateStyle: 'medium' }),
      label: null, // No label needed for delivered
    };
  }

  // If actually shipped, check for expected delivery first
  if (shipment.shippedAt && shipment.expectedDeliveredOn) {
    return {
      date: shipment.expectedDeliveredOn.toLocaleString({
        dateStyle: 'medium',
      }),
      label: 'Expected delivery',
    };
  }

  // If actually shipped but no expected delivery, show actual shipped date
  if (shipment.shippedAt) {
    return {
      date: shipment.shippedAt.toLocaleString({ dateStyle: 'medium' }),
      label: isOutbound ? null : 'Shipped', // Label only for inbound (supplier shipped)
    };
  }

  // If not actually shipped, show expected ship date
  if (shipment.expectedShippedOn) {
    return {
      date: shipment.expectedShippedOn.toLocaleString({ dateStyle: 'medium' }),
      label: 'Exp. ship',
    };
  }

  // Fallback: if no actual shipped and no expected ship, show expected delivery if available
  if (shipment.expectedDeliveredOn) {
    return {
      date: shipment.expectedDeliveredOn.toLocaleString({
        dateStyle: 'medium',
      }),
      label: 'Exp. delivery',
    };
  }

  // No dates available
  return {
    date: null,
    label: null,
  };
}

/**
 * Creates tooltip content showing all shipment dates
 */
function getAllDatesContent(shipment: Shipment, isOutbound: boolean) {
  const dates = [
    {
      label: 'Expected ship:',
      date:
        shipment.expectedShippedOn?.toLocaleString({ dateStyle: 'medium' }) ||
        null,
    },
    {
      label: 'Expected delivery:',
      date:
        shipment.expectedDeliveredOn?.toLocaleString({ dateStyle: 'medium' }) ||
        null,
    },
    // Carrier-reported ETA (from the latest Shippo webhook payload). Sits
    // immediately after "Expected delivery:" so users can compare the
    // self-reported expectation against what the carrier is now signalling.
    {
      label: 'Carrier ETA:',
      date: shipment.eta?.toLocaleString({ dateStyle: 'medium' }) || null,
    },
    {
      label: 'Shipped:',
      date: shipment.shippedAt?.toLocaleString({ dateStyle: 'medium' }) || null,
    },
    {
      label: 'Delivered:',
      date:
        shipment.deliveredAt?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    // Only show received date for inbound shipments
    ...(!isOutbound
      ? [
          {
            label: 'Received:',
            date:
              shipment.receivedAt?.toLocaleString({ dateStyle: 'medium' }) ||
              null,
          },
        ]
      : []),
    // Show cancelled date if cancelled
    ...(shipment.cancelledAt
      ? [
          {
            label: 'Cancelled:',
            date:
              shipment.cancelledAt?.toLocaleString({ dateStyle: 'medium' }) ||
              null,
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={0.25}>
      {dates.map(({ label, date }) => (
        <DateInfoRow key={label} label={label} date={date} />
      ))}
      {shipment.cancellationReason && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="subtitle2">Cancel reason:</Typography>
          <Typography variant="body2">{shipment.cancellationReason}</Typography>
        </>
      )}
    </Stack>
  );
}

/**
 * Props for the ShipmentTableRow component.
 */
interface Props {
  /**
   * The shipment to display in the row.
   * Contains all information about the shipment including its
   * tracking number, locations, and timestamps.
   */
  shipment: Shipment;

  /**
   * The type of shipment (inbound or outbound).
   */
  shipmentType?: 'inbound' | 'outbound';

  handleMarkDelivered: (shipment: Shipment) => void;
  handleMarkReceived: (shipment: Shipment) => void;
  handleShipShipment: (shipment: Shipment) => void;
  handleCancelShipment: (shipment: Shipment) => void;
}

/**
 * A table row component that displays shipment information.
 * Renders shipment details in a responsive format, adapting the display
 * based on screen size.
 *
 * Features:
 * - Clickable shipment ID linking to shipment details (future)
 * - Responsive column visibility based on screen size
 * - Date formatting for shipment milestones
 * - Fallback displays for missing data
 *
 * @param props - Configuration options for the row
 */
export function ShipmentTableRow(props: Props) {
  const navigate = useNavigate();
  const rowSliceSize = 4;
  // Setup the state for how many rows to show for each item
  const [itemsToShow, setItemsToShow] = useState(rowSliceSize);

  // Quarantined lines sort first so a held unit is never hidden behind the
  // "and N more" truncation.
  const orderedItems = useMemo(
    () =>
      [...props.shipment.items].sort(
        (a, b) => b.quarantinedQuantity - a.quarantinedQuantity
      ),
    [props.shipment.items]
  );

  const isOutbound = props.shipmentType === 'outbound';
  const displayStatus = props.shipment.status;
  const status = SHIPMENT_STATUS_CONFIG[displayStatus];
  const relevantDate = getRelevantDate(props.shipment, isOutbound);
  const dateBlock = relevantDate.date ? (
    <Stack alignItems="flex-end">
      <Typography variant="body2" color="text.secondary">
        {relevantDate.label && `${relevantDate.label}`}
      </Typography>
      <Typography variant="body2">{relevantDate.date}</Typography>
    </Stack>
  ) : (
    <Typography variant="body2" color="text.secondary">
      No date set
    </Typography>
  );

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.metaKey || event.ctrlKey) {
      const typeQuery = props.shipmentType ? `?type=${props.shipmentType}` : '';
      window.open(`/shipments/${props.shipment.key}${typeQuery}`, '_blank');
    } else {
      navigate({
        to: '/shipments/$shipmentKey',
        params: { shipmentKey: props.shipment.key },
        search: props.shipmentType ? { type: props.shipmentType } : undefined,
      });
    }
  };

  const handleMarkReceivedClick = () => {
    props.handleMarkReceived(props.shipment);
  };

  return (
    <TableRow onClick={handleRowClick} hover sx={{ cursor: 'pointer' }}>
      {/* References column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="subtitle2">
            {props.shipment.shipmentNumber}
          </Typography>
          <MissingShipmentChip
            missingSince={props.shipment.missingSince}
            thirdPartyBound={props.shipment.thirdPartyBound}
          />
        </Stack>
        <Stack
          direction="row"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={0.3}
        >
          {isOutbound ? (
            <>
              <Typography variant="subtitle2">FO:</Typography>
              {props.shipment.fulfillmentOrder ? (
                <Link
                  to="/fulfillments/$fulfillmentKey"
                  params={{
                    fulfillmentKey: props.shipment.fulfillmentOrder.key,
                  }}
                  variant="body2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {props.shipment.fulfillmentOrder.fulfillmentOrderNumber}
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not set
                </Typography>
              )}
            </>
          ) : props.shipment.purchaseOrder ? (
            <>
              <Typography variant="subtitle2">PO:</Typography>
              <Link
                to="/purchase-orders/$purchaseOrderKey"
                params={{
                  purchaseOrderKey: props.shipment.purchaseOrder.key,
                }}
                variant="body2"
                onClick={(e) => e.stopPropagation()}
              >
                {props.shipment.purchaseOrder.purchaseOrderNumber}
              </Link>
            </>
          ) : props.shipment.returnOrder ? (
            <>
              <Typography variant="subtitle2">RO:</Typography>
              <Link
                to="/return-orders/$returnOrderKey"
                params={{
                  returnOrderKey: props.shipment.returnOrder.key,
                }}
                variant="body2"
                onClick={(e) => e.stopPropagation()}
              >
                {props.shipment.returnOrder.returnOrderNumber}
              </Link>
            </>
          ) : (
            <>
              <Typography variant="subtitle2">PO:</Typography>
              <Typography variant="body2" color="text.secondary">
                Not set
              </Typography>
            </>
          )}
        </Stack>
        <Stack
          direction="row"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={0.3}
        >
          <Typography variant="subtitle2">Tracking:</Typography>
          {props.shipment.trackingNumberDetails?.trackingNumber ? (
            <ShippingCarrierTrackingLabel
              carrier={props.shipment.trackingNumberDetails.carrier}
              trackingIdentifier={
                props.shipment.trackingNumberDetails.trackingNumber
              }
              linkNumberDirectly={true}
              variant="body2"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          )}
        </Stack>
        {props.shipment.serviceLevelName && (
          <Stack
            direction="row"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={0.3}
          >
            <Typography variant="subtitle2">Service level:</Typography>
            <Typography variant="body2">
              {props.shipment.serviceLevelName}
            </Typography>
          </Stack>
        )}
      </TableCell>

      {/* From/to location column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <LocationPairCell
          fromLocation={props.shipment.fromLocation}
          toLocation={props.shipment.toLocation}
        />
      </TableCell>

      {/* Items column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        {orderedItems.slice(0, itemsToShow).map((lineItem) => (
          <Stack
            key={lineItem.key}
            direction="row"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={0.3}
          >
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={0.5}
            >
              <ShipmentItemTypeIcon item={lineItem} />
              <ConditionalTooltip
                arrow
                tooltipText={lineItem.externalReference ?? undefined}
              >
                <Typography
                  variant="body2"
                  sx={
                    lineItem.externalReference
                      ? {
                          textDecoration: 'underline',
                          textDecorationStyle: 'dotted',
                        }
                      : undefined
                  }
                >
                  {lineItem.item.sku}
                </Typography>
              </ConditionalTooltip>
              <Typography variant="body2" color="text.secondary">
                ({lineItem.item.name})
              </Typography>
              {renderQuantityDisplay(lineItem, props.shipment.receivedAt)}
              {lineItem.quarantinedQuantity > 0 && (
                <Box sx={{ marginLeft: 0.5 }}>
                  <QuarantineShipmentChip
                    openCount={lineItem.quarantinedQuantity}
                    shipmentKey={props.shipment.key}
                  />
                </Box>
              )}
              <MissingShipmentChip
                missingSince={props.shipment.missingSince}
                thirdPartyBound={props.shipment.thirdPartyBound}
              />
              {lineItem.pendingSerialCount > 0 && (
                <Tooltip
                  arrow
                  title={`Pending serials: ${formatNumber(lineItem.pendingSerialCount)}`}
                >
                  <Chip
                    label={formatNumber(lineItem.pendingSerialCount)}
                    size="small"
                    color="info"
                    sx={{
                      height: '20px',
                      fontSize: '0.75rem',
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Stack>
        ))}

        {/* Show the "more" button if there are additional items */}
        {itemsToShow < orderedItems.length && (
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              setItemsToShow((prev) => prev + rowSliceSize);
            }}
          >
            and {orderedItems.length - itemsToShow} more{' '}
            {pluralize(orderedItems.length - itemsToShow, {
              singular: 'item',
              plural: 'items',
              includeCount: false,
            })}
          </Typography>
        )}
      </TableCell>

      <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
        <ShipmentTotalValue
          actualValue={props.shipment.totalValue}
          estimatedValue={props.shipment.estimatedTotalValue}
          unconfirmedValue={shipmentUnconfirmedTotal(props.shipment.items)}
        />
      </TableCell>

      <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
        {formatCurrencyValue(props.shipment.shippingCost)}
      </TableCell>

      {/* Time in status column */}
      <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
        <ShipmentTimeInStatus shipment={props.shipment} />
      </TableCell>

      {/* Status and actions column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          {/* Status chip and relevant date */}
          <Tooltip
            arrow
            placement="left"
            title={getAllDatesContent(props.shipment, isOutbound)}
          >
            <Stack
              direction="column"
              alignItems="stretch"
              gap={0.25}
              width="100%"
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                flexWrap="wrap"
              >
                <StatusChip
                  variant={status.variant}
                  color={status.color}
                  label={status.label}
                />
                {dateBlock}
              </Stack>
            </Stack>
          </Tooltip>

          {/* Action buttons */}
          <Stack direction="row" alignItems="center" gap={1} width="100%">
            {canMarkReceived(props.shipment) && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<InventoryOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkReceivedClick();
                }}
                sx={{ width: '100%' }}
              >
                Mark received
              </Button>
            )}
            {canMarkDelivered(props.shipment) && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<KeyboardTabOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  props.handleMarkDelivered(props.shipment);
                }}
                sx={{ width: '100%' }}
              >
                Mark delivered
              </Button>
            )}
            {canMarkShipped(props.shipment) && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<LocalShippingOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  props.handleShipShipment(props.shipment);
                }}
                sx={{ width: '100%' }}
              >
                Mark shipped
              </Button>
            )}
            {canCancel(props.shipment) && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  props.handleCancelShipment(props.shipment);
                }}
                sx={{ width: '100%' }}
              >
                Cancel
              </Button>
            )}
          </Stack>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
